import { ObservableMap } from '../lib/map/observableMap';
import { Verdict } from 'inter-extension-control/module/src/models/verdict';
import { getVerdictByRequestId, ProcessedVerdict } from '../navigation/verdict';
import { ProcessUrlResponse } from '../types';

export type DelegationVerdict = {
  safeSearch: boolean;
  verdict: Verdict;
};
// List of received delegation verdicts
const verdictCache = new ObservableMap<string, DelegationVerdict>();

// Time for waiting a delegation verdict
const DELEGATION_VERDICT_TIMEOUT_MS = 5000;

/**
 * Gets the delegation verdict for a requestId
 *
 * If the delegation verdict has already been received, will return it right away.
 * If the delegation verdict has NOT been received yet, will return it as soon as it's received, with a maximum wait of 5 seconds.
 * If +5 seconds passed and no verdict was received, will return undefined.
 */
export const getDelegationVerdict = async (
  requestId: string
): Promise<DelegationVerdict | undefined> => {
  // Return delegation verdict if it's already available
  const cachedVerdict = verdictCache.get(requestId);
  if (cachedVerdict) {
    return cachedVerdict;
  }

  // Return delegation verdict when available, if not undefined.
  return await verdictCache.waitForValue(
    requestId,
    value => value !== undefined,
    DELEGATION_VERDICT_TIMEOUT_MS
  );
};

/**
 * Saves the delegation verdict and returns the final verdict.
 *
 * @param requestId - ID of the request
 * @param safeSearch - Redirected (or not) to Safe Search by Linewize
 * @param verdict - Verdict applied by Linewize
 */
export const onNewDelegationVerdict = async (
  requestId: string,
  safeSearch: boolean,
  verdict: Verdict
): Promise<Verdict> => {
  // Save the verdict to the cache
  verdictCache.set(requestId, { safeSearch, verdict });

  // Wait for the final verdict and send it
  const processedVerdict = await getVerdictByRequestId(requestId);

  if (isTimeBlockedVerdict(processedVerdict)) {
    // Our extension blocked the request by time
    return Verdict.BlockedByTime;
  }

  if (isBlockedVerdict(processedVerdict)) {
    // Our extension blocked the request
    return Verdict.Blocked;
  }

  // Our extension allowed the request
  return Verdict.Allowed;
};

const isTimeBlockedVerdict = (processedVerdict?: ProcessedVerdict) =>
  processedVerdict?.verdict &&
  [
    ProcessUrlResponse.BLOCK_BY_SCHEDULE,
    ProcessUrlResponse.BLOCK_BY_QUOTA,
  ].includes(processedVerdict.verdict);

const isBlockedVerdict = (processedVerdict?: ProcessedVerdict) =>
  processedVerdict?.verdict == ProcessUrlResponse.BLOCK;
